import { useEffect, useState } from 'react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { GatedButton } from '../ui/GatedButton'
import { TextArea } from '../ui/Input'
import { SectionLabel } from '../ui/SectionLabel'
import { useToast } from '../../contexts/ToastContext'
import { useCreateKudos, checkKudosEligibility } from '../../hooks/useSafety'
import { apiError } from '../../lib/api/client'
import type { CotKudosReason } from '../../types/community'

const REASONS: Array<{ value: CotKudosReason; label: string; hint: string }> = [
  { value: 'Kind', label: 'Kind to someone', hint: 'Looked out for a player who was having a hard time.' },
  { value: 'Patient', label: 'Patient teacher', hint: 'Showed someone how without making them feel small.' },
  { value: 'Welcoming', label: 'Made someone welcome', hint: 'Brought a new or shy player into the game.' },
  { value: 'Fair', label: 'Fair player', hint: 'Played straight — no cheating, no gloating.' },
]

interface KudosModalProps {
  open: boolean
  onClose: () => void
  targetName: string
  actingPersonId?: string
  targetPersonId?: string
  targetHandle?: string
}

/** Kudos modal (COTC_SafetyKudos R2 / DESIGN §3.13). 2 steps + sent, six-month guard on open. */
export function KudosModal({ open, onClose, targetName, targetPersonId, targetHandle }: KudosModalProps) {
  const { flash } = useToast()
  const createKudos = useCreateKudos()
  const [step, setStep] = useState<'reason' | 'detail' | 'sent'>('reason')
  const [reason, setReason] = useState<CotKudosReason | null>(null)
  const [note, setNote] = useState('')
  const [checking, setChecking] = useState(true)

  // Six-month guard: opening on an ineligible target flashes and closes (no modal).
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setStep('reason')
    setReason(null)
    setNote('')
    setChecking(true)
    checkKudosEligibility(targetPersonId, targetHandle)
      .then((elig) => {
        if (cancelled) return
        if (!elig.eligible) {
          const when = elig.lastGivenAt ? new Date(elig.lastGivenAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'recently'
          flash(`You gave ${targetName} kudos on ${when} — you can give again after six months.`)
          onClose()
          return
        }
        setChecking(false)
      })
      .catch(() => {
        // If the precheck fails, allow the flow (the server enforces the rule on send).
        if (!cancelled) setChecking(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, targetPersonId, targetHandle, targetName, flash, onClose])

  if (!open || checking) return null

  async function send() {
    if (!reason) return
    try {
      await createKudos.mutateAsync({ targetPersonId, targetHandle, reason, note: note.trim() || undefined })
      setStep('sent')
    } catch (err) {
      flash(apiError(err).message)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Kudos for ${targetName}`}>
      <div className="animate-rise">
        <SectionLabel className="text-moss">Kudos for {targetName}</SectionLabel>

        {step === 'reason' && (
          <div className="mt-3">
            <h2 className="font-serif text-[26px] font-medium text-ink">What did they do well?</h2>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">
              Kudos are public on their profile and permanent. One per person every six months, so pick someone who earned it.
            </p>
            <div className="mt-4 grid gap-2">
              {REASONS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setReason(r.value)}
                  className={`rounded-btn border px-4 py-3 text-left transition-colors ${reason === r.value ? 'border-moss bg-moss-bg' : 'border-line hover:border-moss'}`}
                >
                  <div className="text-[13.5px] font-medium text-ink">{r.label}</div>
                  <div className="mt-0.5 text-[12px] text-ink-muted">{r.hint}</div>
                </button>
              ))}
            </div>
            <div className="mt-5 flex justify-between">
              <Button variant="outline" onClick={onClose}>Never mind</Button>
              <GatedButton gated={!reason} hint="Pick what they did well first." variant="approve" onClick={() => setStep('detail')}>
                Next
              </GatedButton>
            </div>
          </div>
        )}

        {step === 'detail' && (
          <div className="mt-3">
            <h2 className="font-serif text-[26px] font-medium text-ink">Say what happened.</h2>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">
              A screenshot of the moment makes kudos land harder. Optional.
            </p>
            <TextArea
              className="mt-3"
              rows={3}
              placeholder="What made this worth a kudos?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="mt-5 flex justify-between">
              <Button variant="outline" onClick={() => setStep('reason')}>Back</Button>
              <Button variant="approve" onClick={send} disabled={createKudos.isPending}>Send kudos</Button>
            </div>
          </div>
        )}

        {step === 'sent' && (
          <div className="mt-3 rounded-card bg-moss-bg p-5">
            <h2 className="font-serif text-[26px] font-medium text-moss">Kudos sent.</h2>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">
              It is on their profile now, and their parent was told. You can give {targetName} kudos again in six months — the limit is what makes each one mean something.
            </p>
            <div className="mt-5 flex justify-end">
              <Button variant="ink" onClick={onClose}>Done</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
